import { ensurePrivacySession, getOutboundNickname } from '../../app/zionApp.ts'
import { ensureKeyPair, getPublicKeyBase64 } from '../../storage/keyStore.ts'
import { getInboundQueueId } from '../../storage/sessionStore.ts'
import { resolveDailyCode, sendPairRequest } from '../../socket2/ZionSocketClient.ts'
import { t } from '../../i18n/index.ts'
import { el, escapeHtml, setStatus } from '../dom.ts'
import { showSendPairingModal } from '../modals.ts'
import { navigate } from '../router.ts'

export async function renderSearchScreen(root: HTMLElement): Promise<void> {
  root.innerHTML = ''
  root.append(
    el(`
      <header class="topbar premium-topbar">
        <button type="button" class="icon-btn" id="btn-back">←</button>
        <h2 class="topbar-title">${escapeHtml(t('search_title'))}</h2>
      </header>
    `),
  )

  const card = el(`
    <section class="card pair-card">
      <label for="search-q">${escapeHtml(t('search_label'))}</label>
      <input id="search-q" type="search" placeholder="${escapeHtml(t('search_placeholder'))}" autocapitalize="characters" />
      <button type="button" id="btn-search">${escapeHtml(t('search_btn'))}</button>
      <p class="status muted small">${escapeHtml(t('search_hint'))}</p>
      <p id="search-status" class="status muted"></p>
      <div id="search-results"></div>
    </section>
  `)
  root.append(card)

  root.querySelector('#btn-back')!.addEventListener('click', () => navigate('home'))

  const statusEl = card.querySelector('#search-status') as HTMLElement
  const resultsEl = card.querySelector('#search-results') as HTMLElement
  const input = card.querySelector('#search-q') as HTMLInputElement

  card.querySelector('#btn-search')!.addEventListener('click', () => {
    void runSearch(input.value, statusEl, resultsEl)
  })
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void runSearch(input.value, statusEl, resultsEl)
    }
  })
}

async function runSearch(
  query: string,
  statusEl: HTMLElement,
  resultsEl: HTMLElement,
): Promise<void> {
  const q = query.trim().toUpperCase()
  if (!q) return

  if (!q.startsWith('ZN-')) {
    setStatus(statusEl, t('search_format_err'), 'err')
    return
  }

  setStatus(statusEl, t('search_resolving'))
  const resolved = await resolveDailyCode(q)
  if (!resolved) {
    setStatus(statusEl, t('search_invalid_code'), 'err')
    resultsEl.innerHTML = ''
    return
  }

  resultsEl.innerHTML = ''
  setStatus(statusEl, t('search_session_found'), 'ok')
  showSendPairingModal({
    codeLabel: q,
    onConfirm: () => sendPairTo(resolved.sessionToken, statusEl),
  })
}

async function sendPairTo(peerSession: string, statusEl: HTMLElement): Promise<void> {
  const session = await ensurePrivacySession()
  const queueId = (await getInboundQueueId())?.trim()
  if (!queueId) {
    setStatus(statusEl, t('search_socket_not_ready'), 'err')
    return
  }
  await ensureKeyPair()
  const myNick = await getOutboundNickname()
  const myPub = await getPublicKeyBase64()
  const result = await sendPairRequest({
    toSessionToken: peerSession,
    mySessionToken: session,
    myPublicKey: myPub,
    myNickname: myNick,
    myQueueId: queueId,
  })
  setStatus(
    statusEl,
    result.ok ? t('search_request_sent') : t('search_request_failed'),
    result.ok ? 'ok' : 'err',
  )
  if (result.ok) setTimeout(() => navigate('home'), 600)
}
